import { useState, type ReactNode } from 'react';
import { Button } from '@/components/Button';
import Input from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { MdExpandLess, MdExpandMore, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import type { AgentApiJobSourcesService } from '@/types/serializers';

export type UrlFormValues = {
  urls: string[];
  loginId: string;
  loginPass: string;
};

type Props = {
  service: AgentApiJobSourcesService;
  isSubmitting: boolean;
  onSubmit: (values: UrlFormValues) => void;
  footer?: ReactNode;
};

export default function JobSourceUrlForm({ service, isSubmitting, onSubmit, footer }: Props) {
  const [urlValue, setUrlValue] = useState('');
  const [loginId, setLoginId] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoginSectionOpen, setIsLoginSectionOpen] = useState(false);

  // HERP（AGENT_URL）は企業によってログイン要否が異なるため、任意のログイン入力欄を表示する
  const showOptionalLogin = service.auth_type === 'AGENT_URL';

  const handleSubmit = () => {
    if (urlValue.trim() === '') return;
    const urls = urlValue
      .split('\n')
      .map((url) => url.trim())
      .filter((url) => url !== '');
    onSubmit({ urls, loginId: loginId.trim(), loginPass });
  };

  return (
    <div className="relative mt-sm flex w-full flex-col items-center">
      <div className="flex flex-col items-center gap-x3l">
        <div className="flex flex-col items-center gap-3">
          <p className="text-center text-heading-xl">{service.name}のURLを改行して入力</p>
          <p className="text-center text-body-md">
            {service.name}のエージェント向けURLを入力することで、求人を取り込むことができます。
          </p>
        </div>
        <Textarea
          className="h-[216px] w-[592px] resize-none"
          placeholder={service.placeholder}
          value={urlValue}
          onChange={(e) => setUrlValue(e.target.value)}
        />
        {showOptionalLogin && (
          <div className="flex w-[592px] flex-col gap-sm rounded border border-solid border-border bg-white px-md py-sm">
            <button
              type="button"
              className="flex w-full cursor-pointer items-center justify-between border-0 bg-transparent p-0 text-left"
              onClick={() => setIsLoginSectionOpen((prev) => !prev)}
              aria-expanded={isLoginSectionOpen}
              aria-controls="linkedAccountAgentUrlLoginSection"
            >
              <span className="flex flex-col gap-1">
                <span className="text-sm font-bold">ログイン情報（任意）</span>
                <span className="text-xs text-[#6D6D6D]">
                  ログインが必要な求人ページの場合のみ入力してください。
                </span>
              </span>
              {isLoginSectionOpen ? (
                <MdExpandLess className="size-6 text-[#6D6D6D]" />
              ) : (
                <MdExpandMore className="size-6 text-[#6D6D6D]" />
              )}
            </button>
            {isLoginSectionOpen && (
              <div id="linkedAccountAgentUrlLoginSection" className="mt-xs flex flex-col gap-md">
                <div className="flex flex-col gap-xs">
                  <label htmlFor="linkedAccountAgentUrlLoginId" className="text-sm font-bold">
                    ログインID（メールアドレス）
                  </label>
                  <Input
                    id="linkedAccountAgentUrlLoginId"
                    name="linkedAccountAgentUrlLoginId"
                    className="h-xxl w-full rounded border border-border bg-white px-3 text-sm focus:border-primary focus:outline-hidden"
                    type="text"
                    value={loginId}
                    onChange={(e) => setLoginId(e.target.value)}
                    autoComplete="one-time-code"
                  />
                </div>
                <div className="flex flex-col gap-xs">
                  <label htmlFor="linkedAccountAgentUrlLoginPassword" className="text-sm font-bold">
                    ログインパスワード
                  </label>
                  <div className="relative">
                    <Input
                      id="linkedAccountAgentUrlLoginPassword"
                      name="linkedAccountAgentUrlLoginPassword"
                      className="h-xxl w-full rounded border border-border bg-white px-3 pr-10 text-sm focus:border-primary focus:outline-hidden"
                      type={showPassword ? 'text' : 'password'}
                      value={loginPass}
                      onChange={(e) => setLoginPass(e.target.value)}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="absolute top-1/2 right-3 size-6 -translate-y-1/2 cursor-pointer text-[#6D6D6D] hover:text-[#262626]"
                      onClick={() => setShowPassword(!showPassword)}
                      aria-label={showPassword ? 'パスワードを非表示' : 'パスワードを表示'}
                    >
                      {showPassword ? (
                        <MdVisibility className="size-6" />
                      ) : (
                        <MdVisibilityOff className="size-6" />
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        <div className="flex w-full flex-col items-center gap-md">
          <Button
            type="button"
            disabled={urlValue === '' || isSubmitting}
            onClick={handleSubmit}
            className="h-[52px] w-[208px]"
          >
            求人を取り込み同期する
          </Button>
          {footer}
        </div>
      </div>
    </div>
  );
}
